
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TherapistFAQ = () => {
  const { t } = useLanguage();

  const faqs = [
    {
      question: "What are the requirements to join the platform?",
      answer: "To join our platform, you must have completed Dr. José Luis Marín's Master in Brief Psychotherapy program, hold a valid professional license, have minimum 2 years of clinical experience, maintain professional liability insurance, and have a reliable internet connection for video sessions."
    },
    {
      question: "How do I set my rates and availability?",
      answer: "Once accepted to the platform, you can set your own hourly rates and manage your availability through your therapist dashboard. You have complete control over your schedule and pricing."
    },
    {
      question: "What commission does the platform take?",
      answer: "We charge a 10% commission on completed online sessions. This helps us maintain the platform, provide technical support, and continue improving our services."
    },
    {
      question: "How do I receive payments?",
      answer: "Payments are processed securely through our platform and transferred to your designated bank account. You can track your earnings and payment history in your therapist dashboard."
    },
    {
      question: "Can I offer both online and in-person sessions?",
      answer: "Yes, you can choose to offer online sessions, in-person sessions, or both. You can specify your preferences in your profile and availability settings."
    },
    {
      question: "What technical support is available?",
      answer: "We provide 24/7 technical support for any platform-related issues. Our support team can help with video session problems, payment questions, and general platform navigation."
    },
    {
      question: "How are client reviews handled?",
      answer: "Clients can leave reviews after completed sessions. All reviews are moderated to ensure they are constructive and professional. Positive reviews help improve your visibility on the platform."
    },
    {
      question: "Can I practice internationally?",
      answer: "Yes, our platform allows you to connect with clients worldwide. However, you're responsible for ensuring you comply with licensing requirements in your jurisdiction and any applicable international regulations."
    },
    {
      question: "What happens if I need to cancel a session?",
      answer: "You can cancel sessions through the platform with appropriate notice. Our cancellation policy protects both therapists and clients while allowing flexibility for unexpected circumstances."
    },
    {
      question: "How do I update my profile and credentials?",
      answer: "You can update your profile, credentials, specializations, and other information at any time through your therapist dashboard. Changes are reviewed and approved by our team."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Therapist FAQ - Questions & Answers | Global Therapy Hub</title>
        <meta name="description" content="Find answers to frequently asked questions about joining and using our therapist platform." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Therapist FAQ
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to the most frequently asked questions about joining and 
              using our global therapist platform.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Still have questions?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <div className="space-y-4">
              <p className="text-gray-700">
                <strong>Email:</strong> therapists@psychotherapist.help
              </p>
              <p className="text-gray-700">
                <strong>Support Hours:</strong> Monday - Friday, 9 AM - 6 PM (UTC)
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TherapistFAQ;
